import { type FormEvent, useEffect, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { AlertCircle } from 'lucide-react'

import { getStripeInstance } from '@/lib/stripe-loader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface StripePaymentFormProps {
  connectedAccountId: string
  amountCents: number
  onPaymentMethodCreated: (pmId: string) => void
  onError: (error: string) => void
  disabled?: boolean
  isProcessing?: boolean
}

export function StripePaymentForm(props: StripePaymentFormProps) {
  const [stripePromise] = useState(() => getStripeInstance(props.connectedAccountId))
  const [isStripeUnavailable, setIsStripeUnavailable] = useState(false)

  useEffect(() => {
    let isMounted = true

    stripePromise
      .then(stripe => {
        if (isMounted && !stripe) {
          setIsStripeUnavailable(true)
          props.onError('Unable to initialize Stripe for this connected account')
        }
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return
        }

        const message = error instanceof Error ? error.message : 'Unable to initialize Stripe for this connected account'
        setIsStripeUnavailable(true)
        props.onError(message)
      })

    return () => {
      isMounted = false
    }
  }, [props.connectedAccountId, props.onError, stripePromise])

  if (isStripeUnavailable) {
    return (
      <Card className='border border-destructive/20'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-base text-destructive'>
            <AlertCircle className='h-4 w-4' />
            Stripe unavailable
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-muted-foreground'>
            We could not initialize Stripe for this account. Please try again later.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: 'payment',
        amount: props.amountCents,
        currency: 'usd',
        paymentMethodCreation: 'manual'
      }}
    >
      <StripePaymentFormInner {...props} />
    </Elements>
  )
}

function StripePaymentFormInner({
  onPaymentMethodCreated,
  onError,
  disabled = false,
  isProcessing = false,
}: StripePaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isElementReady, setIsElementReady] = useState(false)

  const isStripeLoading = !stripe || !elements
  const isButtonDisabled = disabled || isStripeLoading || isSubmitting || isProcessing

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!stripe || !elements) {
      onError('Stripe is still loading. Please try again in a moment.')
      return
    }

    setIsSubmitting(true)

    const { error: submitError } = await elements.submit()
    if (submitError) {
      onError(submitError.message ?? 'Validation failed')
      setIsSubmitting(false)
      return
    }

    const result = await stripe.createPaymentMethod({ elements })

    if (result.error) {
      onError(result.error.message ?? 'Unable to create payment method')
      setIsSubmitting(false)
      return
    }

    if (!result.paymentMethod?.id) {
      onError('Stripe did not return a payment method ID')
      setIsSubmitting(false)
      return
    }

    onPaymentMethodCreated(result.paymentMethod.id)
    setIsSubmitting(false)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base font-medium'>Payment Method</CardTitle>
      </CardHeader>
      <CardContent>
        <form className='space-y-4' onSubmit={handleSubmit}>
          <div className='relative'>
            {!isElementReady && (
              <div className='space-y-3 rounded-md border bg-muted/30 p-4'>
                <div className='h-4 w-1/3 animate-pulse rounded bg-muted' />
                <div className='h-10 w-full animate-pulse rounded bg-muted' />
                <div className='h-10 w-full animate-pulse rounded bg-muted' />
              </div>
            )}

            <div className={!isElementReady ? 'absolute inset-0 opacity-0' : ''}>
              <PaymentElement onReady={() => setIsElementReady(true)} />
            </div>
          </div>

          <Button className='w-full' disabled={isButtonDisabled} isLoading={isSubmitting || isProcessing} type='submit'>
            {isStripeLoading && !isSubmitting
              ? 'Loading Stripe'
              : isProcessing
                ? 'Processing Payment...'
                : 'Continue with Payment'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
